import type { KeyboardEvent, PropsWithChildren, Ref } from 'react';
import { RectanglePortraitStackIcon } from '@zep-us/zus-icons';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import '@/shared/features/quiz-folder/consts/events.quiz-folder';
import { useImpressionRef } from '@zep/logger/react';
import { trackEvent } from '@zep/logger/tracking';
import { AuthorTag } from '@/components/AuthorTag';
import { useFormatLargeNumber } from '@/hooks/number/useLargeNumber';
import type { FolderCardPlace } from '@/shared/features/quiz-folder/consts/events.quiz-folder';
import { cn } from '@/utils/tailwind.util';
import type { FolderSummary } from '../types/folder.types';
import { FolderCardMenu } from './FolderCardMenu';
import { FolderCategoryChips } from './FolderCategoryChips';
import { FolderThumbnail } from './FolderThumbnail';
interface FolderCardProps {
  folder: FolderSummary;
  /**
   * 크롤러가 따라갈 수 있어야 하는 목록(자료실)은 이 값을 준다. 내 폴더처럼 로그인 뒤에만 보이는
   * 목록은 `onClick` 으로 충분하다.
   */
  href?: string;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** 남의 폴더가 섞이는 목록에서만 켠다. 내 폴더 목록에서는 내 이름만 반복돼 소음이 된다. */
  showAuthor?: boolean;
  /** 이 카드가 놓인 목록. 클릭을 목록별로 나눠 본다. */
  place: FolderCardPlace;
  /** 목록 안 순번(0부터). 자료실처럼 순위가 의미 있는 목록만 넘긴다. */
  position?: number;
}
export const FolderCard = ({
  folder,
  href,
  onClick,
  onEdit,
  onDelete,
  showAuthor = false,
  place,
  position
}: FolderCardProps) => {
  // 노출은 자료실에서만 센다 — 공개 폴더 상세 진입률의 분모다. 콜백 밖에서 좁혀 두면 안에서 다시 검사하지 않는다.
  const libraryPosition = place === 'library' ? position : undefined;
  const shouldTrackImpression = libraryPosition !== undefined;
  const impressionRef = useImpressionRef<HTMLAnchorElement>(() => {
    if (libraryPosition === undefined) return;
    trackEvent('quiz_folder_card_impression', {
      place: 'library',
      folder_id: folder.id,
      position: libraryPosition
    });
  });
  const handleClick = () => {
    trackEvent('quiz_folder_card_click', {
      place,
      folder_id: folder.id,
      position
    });
    onClick?.();
  };
  return <CardFrame href={href} onClick={href || onClick ? handleClick : undefined} label={folder.name} linkRef={shouldTrackImpression ? impressionRef : undefined} data-sentry-element="CardFrame" data-sentry-component="FolderCard" data-sentry-source-file="FolderCard.tsx">
      <FolderThumbnail imageUrls={folder.thumbnailUrls} data-sentry-element="FolderThumbnail" data-sentry-source-file="FolderCard.tsx" />
      <div className={cn('flex min-h-[157px] w-full min-w-px flex-1 flex-col justify-center gap-xs', 'border-b border-alternative py-lg', 'max-tablet-lg:min-h-[116px] max-tablet-lg:gap-sm max-tablet-lg:py-md')}>
        <div className={'flex flex-col gap-2xs pr-[20px] max-tablet-lg:gap-3xs max-tablet-lg:pr-none'}>
          <div className={'flex w-full justify-between gap-2xs max-tablet-sm:h-[32px] max-tablet-sm:items-center max-tablet-sm:gap-3xs'}>
            <div className={'flex min-w-0 flex-1 items-center overflow-hidden'}>
              <FolderCategoryChips chips={folder.chips} data-sentry-element="FolderCategoryChips" data-sentry-source-file="FolderCard.tsx" />
            </div>
            {(onEdit || onDelete) && <FolderCardMenu onEdit={onEdit} onDelete={onDelete} />}
          </div>
          {/* 제목은 한 줄로 자른다 — 두 줄이면 같은 줄 카드와 높이가 어긋난다. */}
          <p className="word-break-all line-clamp-1 max-w-full text-body-lg-bold text-default tablet-sm:text-heading-sm-bold">
            {folder.name}
          </p>
        </div>
        {/*
          작성자와 메타 묶음. `QuizListItem` 의 `CardContentBottom` 과 값이 같아야 한다 —
          자료실은 두 종류를 한 그리드에 섞으므로 여기가 어긋나면 행 높이가 갈린다.
           퀴즈 쪽은 `isPortable` 로 분기하지만 여기서는 CSS(`max-tablet-lg`)로 같은 결과를 낸다.
         */}
        <div className={cn('flex w-full flex-col gap-2xs laptop-sm:min-h-[40px]', 'max-tablet-lg:flex-row max-tablet-lg:items-center max-tablet-lg:gap-none')}>
          {showAuthor && <AuthorTag authorName={folder.ownerName} authorHashId={folder.ownerId} className={'max-mobile-lg:text-caption-lg-medium'} />}
          <FolderMetaInfo folder={folder} data-sentry-element="FolderMetaInfo" data-sentry-source-file="FolderCard.tsx" />
        </div>
      </div>
    </CardFrame>;
};

/**
 * 행 껍데기. `href` 가 있으면 앵커로, 없으면 키보드로도 열 수 있는 행으로 그린다.
 * 지오메트리는 `QuizListItem` 의 행과 같은 값이라 자료실에서 두 종류가 섞여도 정렬선이 맞는다.
 */
const CardFrame = ({
  href,
  onClick,
  label,
  linkRef,
  children
}: PropsWithChildren<{
  href?: string;
  onClick?: () => void;
  label: string;
  linkRef?: Ref<HTMLAnchorElement>;
}>) => {
  const className = cn('group relative flex w-full items-center gap-md px-3xs py-none', 'max-tablet-lg:gap-sm', (href || onClick) && 'cursor-pointer hover:bg-neutral');
  if (href) {
    return <Link ref={linkRef} prefetch={false} href={href} className={className} onClick={onClick}>
        {children}
      </Link>;
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.key !== 'Enter') return;
    event.preventDefault();
    onClick?.();
  };
  return <div className={className} onClick={onClick} onKeyDown={onClick ? onKeyDown : undefined} role={onClick ? 'link' : undefined} tabIndex={onClick ? 0 : undefined} aria-label={onClick ? label : undefined} data-sentry-component="CardFrame" data-sentry-source-file="FolderCard.tsx">
      {children}
    </div>;
};
const FolderMetaInfo = ({
  folder
}: {
  folder: FolderSummary;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const formatLargeNumber = useFormatLargeNumber();
  return <div className={cn('flex w-full items-center gap-md', 'max-tablet-lg:justify-end max-tablet-lg:gap-xs')} data-sentry-component="FolderMetaInfo" data-sentry-source-file="FolderCard.tsx">
      <span className={'flex shrink-0 items-center gap-3xs text-body-sm-regular text-alternative'}>
        <RectanglePortraitStackIcon size={'2xs'} data-sentry-element="RectanglePortraitStackIcon" data-sentry-source-file="FolderCard.tsx" />
        {t('common:folder.card.quizCount', {
        quizCount: formatLargeNumber(folder.quizsetCount)
      })}
      </span>
    </div>;
};